import {
  Component,
  inject,
  effect,
  signal,
  OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { NotificacionService } from '../../../core/services/notificacion.service';
import { Notificacion } from '../../../core/models/notificacion.model';

const DURACION_MS = 8000;

@Component({
  selector: 'app-notificacion-toast',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './notificacion-toast.html',
  styleUrl: './notificacion-toast.scss',
})
export class NotificacionToast implements OnDestroy {
  private notifService = inject(NotificacionService);
  private router = inject(Router);

  notificacion = this.notifService.toastActual;

  progreso  = signal(100);   // 100 → 0 over DURACION_MS
  visible   = signal(false);

  private timer:     ReturnType<typeof setInterval>  | null = null;
  private cooldown:  ReturnType<typeof setTimeout>   | null = null;
  private currentId: number | null = null;
  private muted = false;   // true durante 1.5s post-cerrar para no encadenar de inmediato

  constructor() {
    effect(() => {
      const n = this.notificacion();
      if (n && n.id !== this.currentId && !this.muted) {
        this.iniciarToast(n);
      } else if (!n && !this.muted) {
        this.visible.set(false);
      }
    });
  }

  private iniciarToast(n: Notificacion): void {
    this.detenerTimer();
    this.currentId = n.id;
    this.progreso.set(100);
    this.visible.set(true);

    const paso = 100 / (DURACION_MS / 50);
    this.timer = setInterval(() => {
      const next = this.progreso() - paso;
      if (next <= 0) {
        this.cerrar();
      } else {
        this.progreso.set(next);
      }
    }, 50);
  }

  cerrar(): void {
    const n = this.notificacion();
    if (n) this.notifService.marcarMostrada(n.id);
    this.visible.set(false);
    this.detenerTimer();

    // Cooldown: impide que la siguiente urgente aparezca de inmediato
    this.muted = true;
    if (this.cooldown) clearTimeout(this.cooldown);
    this.cooldown = setTimeout(() => {
      this.muted = false;
      // Mostrar siguiente urgente si existe
      const next = this.notifService.toastActual();
      if (next) this.iniciarToast(next);
    }, 1500);
  }

  irAccion(): void {
    const n = this.notificacion();
    if (n?.accion) {
      this.router.navigate([n.accion.ruta]);
    }
    this.cerrar();
  }

  private detenerTimer(): void {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  iconoPorTipo(tipo: string): string {
    return {
      danger:  'pi-exclamation-triangle',
      warning: 'pi-bell',
      success: 'pi-check-circle',
      info:    'pi-info-circle',
    }[tipo] ?? 'pi-bell';
  }

  ngOnDestroy(): void {
    this.detenerTimer();
    if (this.cooldown) clearTimeout(this.cooldown);
  }
}
